import React from "react";
import {
    Box, Button, Chip, Typography,
} from "@mui/material";
import {
    CheckCircleOutline, ErrorOutline, UpdateOutlined, DownloadOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const breadcrumbs = [
    { title: "Shipments", path: "/app/crm/shipment/list" },
    { title: "Import Summary" },
];

const ShipmentImportSummaryScreen = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const {
        results = [],
        leads: originalRows = [],   
        importedCount = 0,
        totalCount = 0,
        listPath = "/app/crm/shipment/list",
    } = state || {};

    const skippedCount = totalCount - importedCount;

    const rows = results.map((result, idx) => {
        const row = originalRows[idx] ?? {};
        return {
            index: idx + 1,
            shipmentId: result.shipmentId || `ROW-${idx + 1}`,
            receiverName: row?.receiver?.name || "—",
            receiverPhone: row?.receiver?.phone || "—",
            sourceBranch: row?.sourceBranch?.name || "—",
            destinationBranch: row?.destinationBranch?.name || "—",
            status: result.status,               
            reason: result.reason || result.message || "—",
        };
    });

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Shipment Bulk Import Summary", 40, 40);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text(
            `Total: ${totalCount}   |   Imported: ${importedCount}   |   Skipped: ${skippedCount}   |   Generated: ${new Date().toLocaleString()}`,
            40,
            60,
        );

        autoTable(doc, {
            startY: 75,
            head: [["#", "Shipment ID", "Receiver", "Phone", "Source", "Destination", "Status", "Reason"]],
            body: rows.map((r) => [
                r.index,
                r.shipmentId,
                r.receiverName,
                r.receiverPhone,
                r.sourceBranch,
                r.destinationBranch,
                r.status === "created" ? "Created" : r.status === "updated" ? "Updated" : "Skipped",
                r.status === "skipped" ? r.reason : "",
            ]),
            styles: { fontSize: 8, cellPadding: 4 },
            headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: "bold" },
            columnStyles: {
                0: { cellWidth: 25 },
                6: { cellWidth: 65 },
            },
            didParseCell(data) {
                if (data.column.index === 6 && data.section === "body") {
                    const val = data.cell.raw;
                    if (val === "Created" || val === "Updated") {
                        data.cell.styles.textColor = [27, 128, 80];
                        data.cell.styles.fontStyle = "bold";
                    } else if (val === "Skipped") {
                        data.cell.styles.textColor = [198, 40, 40];
                        data.cell.styles.fontStyle = "bold";
                    }
                }
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
        });

        doc.save(`shipment-import-summary-${Date.now()}.pdf`);
    };

    if (!results.length) {
        return (
            <BreadcrumbLayout breadcrumbs={breadcrumbs}>
                <BreadcrumbLayout.Paper>
                    <Box p={3}>
                        <Typography color="text.secondary">
                            No import results found. Please go back and run an import first.
                        </Typography>
                    </Box>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        );
    }

    const updatedCount = results.filter((r) => r.status === "updated").length;
    const createdCount = results.filter((r) => r.status === "created").length;

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>

                <Box px={3} pt={3} pb={2} display="flex" gap={2} flexWrap="wrap">
                    <StatCard label="Total Submitted" value={totalCount} color="text.primary" />
                    <StatCard label="Created" value={createdCount} color="success.main" />
                    {updatedCount > 0 && (
                        <StatCard label="Updated" value={updatedCount} color="info.main" />
                    )}
                    <StatCard label="Skipped / Failed" value={skippedCount} color="error.main" />
                </Box>

                <Box px={3} pb={3}>
                    <Box
                        sx={{
                            overflowX: "auto",
                            overflowY: "auto",
                            maxHeight: 520,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                        }}
                    >
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                            <thead>
                                <tr>
                                    {["#", "Shipment ID", "Receiver", "Phone", "Source Branch", "Destination Branch", "Status", "Reason"].map((h) => (
                                        <th key={h} style={thStyle(theme)}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.index} style={trStyle(theme, row.index, row.status === "skipped")}>
                                        <td style={tdStyle(theme)}>{row.index}</td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.shipmentId}
                                            </Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2">{row.receiverName}</Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.receiverPhone}
                                            </Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2">{row.sourceBranch}</Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            <Typography variant="body2">{row.destinationBranch}</Typography>
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            {row.status === "created" && (
                                                <Chip
                                                    icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                                                    label="Created"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                            {row.status === "updated" && (
                                                <Chip
                                                    icon={<UpdateOutlined sx={{ fontSize: 14 }} />}
                                                    label="Updated"
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            )}
                                            {row.status === "skipped" && (
                                                <Chip
                                                    icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                                                    label="Skipped"
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                />
                                            )}
                                        </td>
                                        <td style={tdStyle(theme)}>
                                            {row.status === "skipped" ? (
                                                <Typography variant="caption" color="error.main">
                                                    {row.reason}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>

                <Box px={3} pb={3} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadOutlined />}
                        onClick={handleDownloadPDF}
                    >
                        Download PDF
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => navigate(listPath)}
                    >
                        Go to Shipments
                    </Button>
                </Box>

            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default ShipmentImportSummaryScreen;

const StatCard = ({ label, value, color }) => (
    <Box
        sx={{
            px: 3, py: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            minWidth: 140,
            textAlign: "center",
        }}
    >
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
);

const thStyle = (theme) => ({
    padding: "10px 12px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    textAlign: "left",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderRight: `1px solid ${theme.palette.divider}`,
});

const trStyle = (theme, idx, isSkipped) => ({
    backgroundColor: isSkipped
        ? theme.palette.mode === "dark"
            ? "rgba(211,47,47,0.12)"
            : "rgba(211,47,47,0.05)"
        : idx % 2 === 0
            ? theme.palette.action.hover
            : theme.palette.background.paper,
});

const tdStyle = (theme) => ({
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: "middle",
});